export const PROCESSED_PACKAGE_COLLECTION = "products_freezedry_package";

export type ProcessMethod = "freeze_dried" | "dehydrated";
export type ProductForm = "whole" | "powder" | "slices" | "capsules";
export type PackageType = "bag" | "can" | "box";

export interface ProcessedPackage {
  firebaseId: string;
  id: string;
  parentId: string;
  packageType: PackageType;
  productForm: ProductForm;
  size: number;
  formatedSize: string;
  packageWeight: number | null;
  material: string;
  packageDimensions: { lengthCm: number; widthCm: number; heightCm: number } | null;
  unitsPerBox: number;
  boundPackageId: string;
  active: boolean;
  ean: string;
  priceStore: number;
  priceRestaurant: number;
  priceRetail: number;
  priceOther: number;
  priceWebshop?: number | null; // SEK per package, including VAT
  vatRate?: number | null; // percentage; VAT amount is derived from the gross price
  sku?: string;
  // Resolve storage instructions and shelf life from the product whose id matches parentId.
  // Older package documents may still contain these fields; new storage data belongs to the product.
  storeData_sv?: string;
  storeData_en?: string;
  storeTemp?: number;
  shelfLife?: string;
  menuName_sv: string;
  menuName_en: string;
  /** Legacy package origins are retained on read only. Batch labels use stock origins. */
  origin_sv?: string;
  origin_en?: string;
  batchLabel?: import("./processedBatchLabels").BatchLabelSettings | null;
  createdAt: number;
}

export const PROCESS_METHODS: { value: ProcessMethod; label: string }[] = [
  { value: "freeze_dried", label: "Frystorkat" },
  { value: "dehydrated", label: "Torkat" },
];
export const PRODUCT_FORMS: { value: ProductForm; label: string }[] = [
  { value: "whole", label: "Hela / bitar" },
  { value: "powder", label: "Pulver" },
  { value: "slices", label: "Skivor" },
  { value: "capsules", label: "Kapslar" },
];
export const PACKAGE_TYPES: { value: PackageType; label: string }[] = [
  { value: "bag", label: "Påse" },
  { value: "can", label: "Burk" },
  { value: "box", label: "Box" },
];

export function methodFromLegacy(product: { processMethod?: ProcessMethod; productType?: string }): ProcessMethod {
  return product.processMethod || (product.productType?.startsWith("dehydrated") ? "dehydrated" : "freeze_dried");
}

export function formFromLegacy(productType?: string): ProductForm {
  if (productType?.includes("slices")) return "slices";
  if (productType?.includes("powder")) return "powder";
  if (productType === "capsules") return "capsules";
  return "whole";
}

export function isProcessedPackageActive(pkg: ProcessedPackage, packages: ProcessedPackage[]): boolean {
  if (pkg.active === false) return false;
  if (pkg.packageType !== "box") return true;
  const parent = packages.find((candidate) => candidate.id === pkg.boundPackageId);
  return !!parent && parent.parentId === pkg.parentId && parent.packageType !== "box" && parent.active !== false;
}

export const processedPackageLabel = (pkg: Pick<ProcessedPackage, "formatedSize" | "size">) => pkg.formatedSize || `${pkg.size} g`;

export function includedVatAmount(grossPrice: number, vatRate: number): number {
  return Math.round((grossPrice * vatRate / (100 + vatRate)) * 100) / 100;
}

export function processedPackageLabelWithParent(pkg: ProcessedPackage, packages: ProcessedPackage[]): string {
  if (pkg.formatedSize) return pkg.formatedSize;
  if (pkg.packageType === "box" && pkg.unitsPerBox > 0) {
    const innerPackage = packages.find((candidate) => candidate.id === pkg.boundPackageId && candidate.parentId === pkg.parentId);
    if (innerPackage) return `${pkg.unitsPerBox} × ${processedPackageLabel(innerPackage)}`;
  }
  return processedPackageLabel(pkg);
}
